import React, { lazy, useEffect, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import _get from "lodash/get"
import debounce from "lodash/debounce"
import { store } from "@/store"
import { apim } from "@/constants/api"
import { getConfig } from "@/constants/config"
import useRegisterOfflineProducti18n from "@/i18n/useRegisterOfflineProducti18n"
import { getShortenedUrlwithGeneral } from "@/utils/helper"
import CONSTANTS from "@/constants/index"
import PageLoader from "@/components/core/PageLoader/PageLoader"
import {
  addReviewRegistrationAnalytics,
  editButtonAnalyticsEventInfo,
  getProductDetails,
  submitRegistrationGenericError,
} from "@/components/RegisterOfflineProduct/v1/RegisterOfflineHelper"
import {
  getGenericListPath,
  getDealerDetails,
} from "@/store/features/genericSlice"
import { selectGenericState, showToast } from "@/store/features/genericSlice"
import {
  clearSuggestion,
  getModelNumberSuggest,
  selectMyAccountState,
  setInvalidModelNumber,
  setSubmitStatus,
} from "@/store/features/myProductSlice"
import RegisterOfflineProductView from "@/components/RegisterOfflineProduct/v1/RegisterOfflineProductView"
import ProductInformation from "@/components/RegisterOfflineProduct/v1/ProductInformation"
import { selectAuthState } from "@/store/features/authSlice"
import { validateRecaptcha } from "@/components/core/ReCaptcha/ReCaptcha"

const RegistrationInformation = lazy(() =>
  import("@/components/RegisterOfflineProduct/v1/RegistrationInformation")
)
const ReviewRegistration = lazy(() =>
  import("@/components/RegisterOfflineProduct/v1/ReviewRegistration")
)

const RegisterOfflineProduct = ({ data: authorData = {} }) => {
  const dispatch = useDispatch()
  const { isAuth, user } = useSelector(selectAuthState)
  const { dealerDetails: dealers = [], placeOfPurchaseList } =
    useSelector(selectGenericState)
  const {
    status = false,

    offlineRegistration: {
      suggestions = [],
      invalidModelNumber = false,
      submitError = false,
      isDisabled = false,
    },
  } = useSelector(selectMyAccountState)

  const staticText = useRegisterOfflineProducti18n(authorData)
  const [customerDetails, setCustomerDetails] = useState({})
  const [productDetails, setProductDetails] = useState([])
  const [searchKeyword, setSearchKeyword] = useState("")
  const [generalCountry, setGeneralCountry] = useState("")
  const [legalPageLinks, setLegalPageLinks] = useState("")
  const [myProductsPath, setMyProductsPath] = useState("")
  const [isSuccess, setIsSuccess] = useState(false)

  useEffect(() => {
    getDealers()
    getConfig().then(config => {
      const country = _get(config, "internationalization.country", "")
      const legalPageLinks = _get(config, "legalPageData.map", {})

      const myProductsPath = getShortenedUrlwithGeneral(
        _get(config, "general.myProductsPath", ""),
        config.general
      )
      setLegalPageLinks(legalPageLinks)
      setGeneralCountry(country)
      setMyProductsPath(myProductsPath ? myProductsPath : "/")
    })
  }, [])

  useEffect(() => {
    if (searchKeyword.length > 2) handleModelNumberSuggestion()
  }, [searchKeyword])

  const getDealers = async () => {
    await store.dispatch(getGenericListPath())
    await store.dispatch(getDealerDetails())
  }

  const submitRegistration = () => {
    const productsDetail = getProductDetails(productDetails)
    const body = {
      firstName: _get(customerDetails, "firstName.value", ""),
      lastName: _get(customerDetails, "lastName.value", ""),
      countryCode: "+91",
      mobile: _get(customerDetails, "phone.value", ""),
      email: _get(customerDetails, "email.value", ""),
      addressLine1: _get(customerDetails, "address.value", ""),
      city: _get(customerDetails, "city.value", ""),
      state: _get(customerDetails, "state.value", ""),
      pincode: _get(customerDetails, "pinCode.value", ""),
      isOnline: false,
      products: productsDetail,
    }

    validateRecaptcha().then(res => {
      if (res.success) {
        apim
          .post(`/crm/product/registration`, body)
          .then(res => {
            const { data: { status = "" } = {} } = res
            if (status === "success") {
              setIsSuccess(true)
              addReviewRegistrationAnalytics(
                "cmp:click",
                "success",
                productDetails,
                ""
              )
            } else {
              setIsSuccess(false)
              submitRegistrationGenericError(
                productDetails,
                authorData.genericError ?? staticText.genericError
              )
            }
          })
          .catch(err => {
            const {
              ERROR_MESSAGE: { GENERAL = "" },
            } = CONSTANTS

            dispatch(
              showToast({
                message: GENERAL,
                isVisible: true,
              })
            )
            submitRegistrationGenericError(
              productDetails,
              authorData.genericError ?? staticText.genericError
            )
            dispatch(
              setSubmitStatus({
                submitError: staticText.serverError,
                isDisabled: false,
              })
            )
            console.error(err)
          })
      } else {
        dispatch(
          showToast({
            message: authorData.genericError ?? staticText?.genericError,
            isVisible: true,
          })
        )
      }
    })
  }

  const handleClearSuggestions = () => {
    setSearchKeyword("")
    dispatch(clearSuggestion())
  }

  const steps = [
    {
      label:
        authorData.registrationInformationLabel ??
        staticText.registrationInformationTitle,
      editButtonAnalytics: editButtonAnalyticsEventInfo(
        staticText.registrationInformationTitle?.toLowerCase()
      ),
      content: RegistrationInformation,
      props: {
        staticText: staticText,
        productDetails: productDetails,
        getCustomerDetails: setCustomerDetails,
        data: customerDetails,
        title: staticText.registrationInformationTitle,
      },
    },
    {
      label:
        authorData.productInformationLabel ??
        staticText.productInformationTitle,
      editButtonAnalytics: editButtonAnalyticsEventInfo(
        staticText.productInformationTitle?.toLowerCase()
      ),
      content: ProductInformation,
      props: {
        staticText: staticText,
        placeOfpurchaseList: placeOfPurchaseList,
        dealersDetail: dealers,
        getProductDetails: setProductDetails,
        productDetails: productDetails,
        getSuggestions: setSearchKeyword,
        clearSuggestions: handleClearSuggestions,
        suggestions: suggestions,
        searchKeyword: searchKeyword,
        invalidModelNumber: invalidModelNumber,
        checkInValidModelNumber: val => dispatch(setInvalidModelNumber(val)),
        title: staticText.productInformationTitle,
        authorData: authorData,
      },
    },
    {
      label: authorData.reviewLabel ?? staticText.reviewTitle,
      content: ReviewRegistration,
      props: {
        staticText: staticText,
        submitRegistration: submitRegistration,
        legalPages: legalPageLinks,
        submitError: submitError,
        isDisabled: isDisabled,
      },
    },
  ]

  const handleModelNumberSuggestion = debounce(() => {
    getModelNumberSuggestions()
  }, 500)

  const getModelNumberSuggestions = () => {
    const params = {
      q: searchKeyword,
      rows: 10,
      fq: ['language_s:("en")', '-Brand_ss:("PlumbingServiceParts")'],
      fl: "variantList.sku_ss,customerFacingSKUList_ss,Brand_ss,Country_ss",
      start: 0,
      skuSearch: true,
    }
    dispatch(getModelNumberSuggest(params))
  }

  return (
    <>
      <PageLoader loading={status === "loading"} />
      {steps.length > 0 && (
        <RegisterOfflineProductView
          staticText={staticText}
          steps={steps}
          productDetails={productDetails}
          customerDetails={customerDetails}
          isSuccess={isSuccess}
          myProductsPath={myProductsPath}
        />
      )}
    </>
  )
}

export default RegisterOfflineProduct
